/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔐 Permissions Middleware — declarative route gating.
///
/// Usage on a page:
///
///   definePageMeta({
///     authenticated: true,
///     requiresPermission: 'users:invite',
///     // — or —
///     requiresPermissions: ['governance:edit', 'governance:view'],
///     requiresPermissionsMode: 'all',  // optional, defaults to 'any'
///     // — or —
///     requiresSuperAdmin: true,
///   })
///
/// Behavior:
///   - Public pages and unauthenticated users are passed through.
///     auth.global.ts is responsible for routing them; this middleware
///     does nothing in that state.
///   - Authenticated users without the required permission are
///     redirected to /forbidden with `?from=<path>` so the page can
///     show a useful "you don't have access to X" message.
///   - SuperAdmins always pass; their permission set includes
///     everything in the catalog.
///
/// Order: Nuxt runs global middleware in alphabetical filename order,
/// so this file (`permissions.global.ts`) runs AFTER `auth.global.ts`
/// — exactly what we want, because the auth gate has already taken
/// care of "is the user signed in / has an account / has finished
/// setup."
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export default defineNuxtRouteMiddleware((to) => {
  // Server-side: skip. Permission state lives in the client store and
  // the underlying Firestore listeners only run client-side. The
  // initial server-rendered HTML never includes pages that need real
  // gating — at worst the user sees a flash of denied content for a
  // few ms while the SPA hydrates, which the middleware's client-side
  // pass then corrects.
  if (import.meta.server) return

  const requiresPermission = to.meta.requiresPermission as string | undefined
  const requiresPermissions = to.meta.requiresPermissions as string[] | undefined
  const requiresPermissionsMode = (to.meta.requiresPermissionsMode as 'any' | 'all' | undefined) || 'any'
  const requiresSuperAdmin = to.meta.requiresSuperAdmin === true

  // No gating declared on this page → pass through.
  if (!requiresPermission && !requiresPermissions?.length && !requiresSuperAdmin) return

  const authStore = useAuthStore()
  // Not signed in → auth.global.ts already handles the redirect to
  // /login; we don't double-up.
  if (!authStore.isLoggedIn) return

  const permissionsStore = usePermissionsStore()

  // Hydration guard: the snapshot listeners that populate this store fire
  // asynchronously after `bind(uid)` runs in the firebase plugin. On a
  // fresh page navigation, the middleware can run BEFORE those listeners
  // fire — at which point `isSuperAdmin` and `granted` are both empty,
  // and we'd redirect a fully-permitted user to /forbidden. Once the
  // store reports `loaded === true`, every subsequent decision is
  // authoritative; until then we let navigation through and rely on the
  // page's own data fetches to fail closed for genuinely unauthorized
  // users (Snowflake's own privilege checks gate the actual data).
  if (!permissionsStore.loaded) return

  // SuperAdmin gate.
  if (requiresSuperAdmin && !permissionsStore.isSuperAdmin) {
    return navigateTo({
      path: '/forbidden',
      query: { from: to.fullPath, missing: 'superAdmin' },
    })
  }

  // Single-permission shortcut.
  if (requiresPermission && !permissionsStore.can(requiresPermission)) {
    return navigateTo({
      path: '/forbidden',
      query: { from: to.fullPath, missing: requiresPermission },
    })
  }

  // Multi-permission case.
  if (requiresPermissions?.length && !permissionsStore.can(requiresPermissions, requiresPermissionsMode)) {
    return navigateTo({
      path: '/forbidden',
      query: {
        from: to.fullPath,
        missing: requiresPermissions.join(','),
        mode: requiresPermissionsMode,
      },
    })
  }
})

// Type augmentation — lets TS recognize the new fields when authors
// add them to definePageMeta.
declare module '#app' {
  interface PageMeta {
    requiresPermission?: string
    requiresPermissions?: string[]
    requiresPermissionsMode?: 'any' | 'all'
    requiresSuperAdmin?: boolean
  }
}

declare module 'vue-router' {
  interface RouteMeta {
    requiresPermission?: string
    requiresPermissions?: string[]
    requiresPermissionsMode?: 'any' | 'all'
    requiresSuperAdmin?: boolean
  }
}

export {}
